import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { DataPlaneMerchantSettings, EndType, FundInfo, PaymentLabel } from '../@types/types';
import { GivingType } from '../shared/constants';
import { getFrequencyOptionsByName } from '../shared/utils';

export function useFormSelectOptions({
	fundLabel,
	hasDefaultFund,
	merchantSettings,
	paymentLabel,
	funds,
	showApplePay,
}: {
	fundLabel?: string;
	hasDefaultFund?: boolean;
	merchantSettings: DataPlaneMerchantSettings;
	paymentLabel: PaymentLabel;
	funds: FundInfo[];
	showApplePay?: boolean;
}) {
	const { t: translate } = useTranslation();
	const { RecurringFrequencies, CryptoLink, StockGivingLink } = merchantSettings;

	const frequencyOptions = useMemo(() => getFrequencyOptionsByName(RecurringFrequencies), [RecurringFrequencies]);

	const endTypeOptions = useMemo(
		() => [
			{ value: EndType.Never.toString(), label: translate('recurring.cancelIt') },
			{
				value: EndType.Occurrences.toString(),
				label: translate('recurring.afterOccurrences', { label: paymentLabel.NounPluralLowerCase }),
			},
			{ value: EndType.Date.toString(), label: translate('recurring.afterDate') },
		],
		[translate, paymentLabel.NounPluralLowerCase]
	);

	const givingTypeOptions = useMemo(() => {
		const options = [
			{
				value: GivingType.CARD_OR_ACH.toString(),
				label: showApplePay ? translate('givingType.cardOrACHOrApplePay') : translate('givingType.cardOrACH'),
			},
			...(CryptoLink
				? [
						{
							value: GivingType.CRYPTOCURRENCY.toString(),
							label: translate('givingType.crypto'),
						},
				  ]
				: []),
			...(StockGivingLink ? [{ value: GivingType.STOCK.toString(), label: translate('givingType.stock') }] : []),
		];
		return options;
	}, [CryptoLink, StockGivingLink, translate, showApplePay]);

	const fundsOptions = useMemo(() => {
		const mapped = funds
			.filter(fnd => fnd.IsVisibleToMerchant)
			.map(fund => ({
				value: fund.FundKey,
				label: fund.FundName,
			}));

		if (!hasDefaultFund) {
			mapped.unshift({
				value: '',
				label: translate(fundLabel ? 'funds.chooseLabelledFund' : 'funds.chooseFund', { label: fundLabel }),
			});
		}

		return mapped;
	}, [fundLabel, funds, hasDefaultFund, translate]);

	return {
		givingTypeOptions,
		frequencyOptions,
		endTypeOptions,
		funds: fundsOptions,
	};
}
